import ExcelJS from 'exceljs/dist/exceljs.bare';

import { customFormatDate } from '../../../common/utils/formatDate';
import { Constants } from './excel-service.constants';
import { Constants as ProductCenterTableWrapperConstants } from '../../product-center-table-wrapper/product-center-table-wrapper.constants';
import { CellFormat } from './excel.service.model';
import { i18nService } from '../../../common/i18n/i18n';
import Locale from '../../../common/utils/locale';
/**
 * Formats a date string into a specific format.
 * If the date is not provided, returns a hyphen.
 * @param date - The date string to format.
 * @returns The formatted date string.
 */
export function formatDate(date?: string | undefined | null): string {
  if (!date) {
    return Constants.HYPHEN;
  }

  return customFormatDate(
    date,
    Constants.DATE_FORMAT_OPTIONS.FORMAT,
    Constants.DATE_FORMAT_OPTIONS.LOCALE,
  ).replace(/ /g, Constants.HYPHEN);
}

/**
 * Returns the name of the cumulative worksheet based on the selected tab in the tile view.
 * @returns The name of the cumulative worksheet.
 */
export async function getCumulativeWorksheetName(
  selectedTabInTileView: number | undefined,
): Promise<string> {
  const localeInstance = new Locale();

  if (!i18nService.isI18nLoadedForLanguage(localeInstance.language)) {
    await new Promise((resolve) => {
      i18nService.loadi18nForLanguage(localeInstance.language, resolve);
    });
  }

  const localize = i18nService.getFixedT(localeInstance.language, null, null);
  switch (selectedTabInTileView) {
    case ProductCenterTableWrapperConstants.TILES_TAB_INDEX.ONE_YEAR:
      return localize(
        ProductCenterTableWrapperConstants.TRANSLATION_KEYS.FUND_RETURN
          .ONE_YEAR,
      );
    case ProductCenterTableWrapperConstants.TILES_TAB_INDEX.THREE_YEARS:
      return localize(
        ProductCenterTableWrapperConstants.TRANSLATION_KEYS.FUND_RETURN
          .THREE_YEARS,
      );
    case ProductCenterTableWrapperConstants.TILES_TAB_INDEX.FIVE_YEARS:
      return localize(
        ProductCenterTableWrapperConstants.TRANSLATION_KEYS.FUND_RETURN
          .FIVE_YEARS,
      );
    case ProductCenterTableWrapperConstants.TILES_TAB_INDEX.TEN_YEARS:
      return localize(
        ProductCenterTableWrapperConstants.TRANSLATION_KEYS.FUND_RETURN
          .TEN_YEARS,
      );
    case ProductCenterTableWrapperConstants.TILES_TAB_INDEX.SINCE_INCEPTION:
      return localize(
        ProductCenterTableWrapperConstants.TRANSLATION_KEYS.FUND_RETURN
          .SINCE_INCEPTION,
      );
    default:
      return localize(
        ProductCenterTableWrapperConstants.TRANSLATION_KEYS.FUND_RETURN
          .ONE_YEAR,
      );
  }
}

/**
 * Formats a cell in an Excel worksheet.
 *
 * @param cell - The Excel cell to format.
 * @param value - The value to be displayed in the cell.
 * @param format - The format to apply to the cell.
 */
export function formatCell(
  cell: ExcelJS.Cell,
  value: any,
  format: CellFormat,
): void {
  if (value == null) {
    cell.value = Constants.HYPHEN;
    cell.alignment = { horizontal: Constants.ALIGNMENT.HORIZONTAL.LEFT };
    return;
  }

  const numericValue =
    typeof value === Constants.TYPE_OF.NUMBER ? value : parseFloat(value);
  if (isNaN(numericValue)) {
    cell.value = Constants.HYPHEN;
    return;
  }

  cell.value = numericValue;
  cell.numFmt =
    format === CellFormat.CURRENCY
      ? Constants.CELL_FORMAT.CURRENCY
      : Constants.CELL_FORMAT.NUMBER;
  cell.alignment = { horizontal: Constants.ALIGNMENT.HORIZONTAL.RIGHT };
}

/**
 * Converts HTML to an array of ExcelJS.RichText objects.
 *
 * @param html The HTML string to convert.
 * @returns An array of ExcelJS.RichText objects.
 */
export function convertHtmlToRichText(html: string): ExcelJS.RichText[] {
  const div = document.createElement(Constants.HTML_TAGS.DIV.OPEN);
  div.innerHTML = html;
  return processRichTextNodes(div);
}

export function processRichTextNodes(
  node: Node,
  font: Partial<ExcelJS.Font> = {},
): ExcelJS.RichText[] {
  const richText: ExcelJS.RichText[] = [];

  const processNode = (n: Node, currentFont: Partial<ExcelJS.Font>) => {
    if (n.nodeType === Node.TEXT_NODE) {
      richText.push({ text: n.textContent || '', font: { ...currentFont } });
    } else if (n.nodeType === Node.ELEMENT_NODE) {
      const element = n as HTMLElement;
      const newFont = updateFontStyles(
        currentFont,
        element.tagName.toLowerCase(),
      );
      Array.from(element.childNodes).forEach((child) =>
        processNode(child, newFont),
      );
    }
  };

  const defaultFont: Partial<ExcelJS.Font> = {
    name: Constants.FONT.TIMES_NEW_ROMAN,
    size: Constants.FONT.SIZE,
    ...font,
  };

  processNode(node, defaultFont);
  return richText;
}

/**
 * Updates the font styles based on the given tagName.
 * @param currentFont The current font styles.
 * @param tagName The HTML tag name.
 * @returns The updated font styles.
 */
export function updateFontStyles(
  currentFont: Partial<ExcelJS.Font>,
  tagName: string,
): Partial<ExcelJS.Font> {
  const font: Partial<ExcelJS.Font> = { ...currentFont };
  switch (tagName) {
    case Constants.HTML_TAGS.B:
    case Constants.HTML_TAGS.STRONG:
      font.bold = true;
      break;
    case Constants.HTML_TAGS.I:
    case Constants.HTML_TAGS.EM:
      font.italic = true;
      break;
    case Constants.HTML_TAGS.A:
      font.color = { argb: Constants.FONT.LINK_COLOR };
      break;
  }
  return font;
}
